<script>
    import {
        SvelteTable,
        LoadingModal,
        NotificationModal,
        CardLayout,
        CollapsibleTableIcons,
        SendQuoteOrInvoiceToPatientModal,
        RichTextEditor,
        DecimalInput,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getNumber,
        getText,
        goToURL,
        getPaymentAmountPaid,
        getFormatDate,
        isObjectEmpty,
        getDisplayTotal,
        showError,
        showException,
        formatPortalUrls,
        goToDownloadPage,
        getInvoiceStatus,
        showPopupNotification,
        openPortalPage,
        showInfo,
        isDateAndTimeInPast,
        getClaimStatus,
        getHHMM,
        getDateForBackend,
        runWithCommunicationsCheck,
        addHoursToDate,
    } from "../../../Utils/utilFunctions.svelte";

    import { allGroups, user } from "../../../stores";

    import {
        addIcon,
        EmptyGroupDetails,
        InsuranceTypesBypassDisabledComms,
        MaxPaperProcessingFee,
        NoteTypes,
    } from "../../../Utils/utilConstants.svelte";

    import {
        getDisplayBillingRuleText,
        getDisplayPaymentDetailsText,
        getEncounterClaimStatusTooltip,
        isHbfWAFullyCoveredWithPatientPayment,
        isSufficientTimeToCreateEstimatesOrInvoices,
        showEncounterPendingClaimTooltip,
        showEncounterRemoveClaimDelayIcon,
    } from "../helpers/encounterHelper.svelte";

    import {
        DirectBillingStatus,
        DirectClaimStatus,
        DirectDoctorStatus,
        DirectEncounterStatus,
        DirectInsuranceType,
        DirectUserRoles,
        PatientPayInsuranceTypes,
    } from "../../../Utils/utilEnums.svelte";

    import { cacheAllGroups } from "../../../Utils/utilCache.svelte";
    import PatientEncounterInvoiceSendDetails from "./PatientEncounterInvoiceSendDetails.svelte";

    export let invoicesData = [],
        patientEncounterDetails = {},
        latestInvoiceData = {},
        paymentsData = [],
        latestInvoiceDataItems = [],
        itemsNotSaved = false,
        isShowSelectBillingRule = false,
        reloadPatientEncounter = () => {},
        reloadBillingDetails = () => {},
        logsLoaded = false,
        totalPaymentsAmount = Number(0);

    export let paymentsTableRef;

    $: hasCurrentInvoice =
        invoicesData.length && invoicesData.some((inv) => inv.status !== 9);

    // No need to check billing rule related properties for Cosmetic encounter with free text code
    $: showCreateInvoiceBtn =
        !isObjectEmpty(latestInvoiceData) &&
        !hasCurrentInvoice &&
        latestInvoiceDataItems.length &&
        (latestInvoiceData.billingCodesType == 2 ||
            !isShowSelectBillingRule ||
            latestInvoiceData.billingType != null);

    $: isUserEnableClaimInvoice =
        $user.role == 100 || $user.role == 80 || $user.role == 20;

    $: hasOPVFailed =
        patientEncounterDetails.opvStatusHealthFund == 2 ||
        patientEncounterDetails.opvStatusMedicare == 2 ||
        patientEncounterDetails.opvStatusVeteran == 2;

    // if insurance type to one that requires OPV check, hide claim btn if any opv check failed
    $: showClaimInvoiceBtn =
        isUserEnableClaimInvoice &&
        !showCreateInvoiceBtn &&
        !latestInvoiceData.patientPaysTotalFee &&
        showClaimInvoiceInsuranceTypes.includes(
            latestInvoiceData.insuranceType,
        );

    /*
        show this button if:
        1. Patient Claim is ON
        2. Claim Status is either Pending, Failed, Rejected or NoNeedToClaim
        3. There is a payment from patient
    */
    $: showPatientClaimBtn =
        isUserEnableClaimInvoice &&
        !showCreateInvoiceBtn &&
        patientEncounterDetails?.patientClaim &&
        [0, 1, 5, 10].includes(patientEncounterDetails?.claimStatus) &&
        !!paymentsData?.length &&
        paymentsData.some((payment) => payment.paidBy == 0);

    $: showClaimViaSendingInvoiceBtn =
        isUserEnableClaimInvoice &&
        !showCreateInvoiceBtn &&
        !invoicesData.some((i) => i.status == 2) && //no invoice was sent
        showClaimViaSendingInvoiceInsuranceTypes.includes(
            latestInvoiceData.insuranceType,
        );

    $: showCreateTopupInvoiceBtn = 
        (
            (
                (
                    [0, 11, 20].includes(latestInvoiceData.patientInsuranceType) || 
                    (latestInvoiceData.patientInsuranceType == 10 && latestInvoiceData.patientInsuranceId) //Insurance type = private / self insured / overseas (not other)
                ) && 
                patientEncounterDetails.billingStatus == 4 && !latestInvoiceData.patientPaysTotalFee // and encounter status = in progress & patientPaysTotalFee = false
            )
            || //or
            (
                (
                    [15, 16, 17, 25, 26, 18, 30].includes(latestInvoiceData.patientInsuranceType) || 
                    (latestInvoiceData.patientInsuranceType == 10 && !latestInvoiceData.patientInsuranceId) //Insurance type = anything else or overseas other fund
                ) && 
                patientEncounterDetails.billingStatus == 4 //and encounter status = in progress
            )
        ) && patientEncounterDetails.insurancePaymentTag == 4; //insurance underpaid
    
    $: showInsuranceDeclinedButton = 
        (
            (
                (
                    [0, 11, 20].includes(latestInvoiceData.patientInsuranceType) || 
                    (latestInvoiceData.patientInsuranceType == 10 && latestInvoiceData.patientInsuranceId) //Insurance type = private / self insured / overseas (not other)
                ) && 
                patientEncounterDetails.billingStatus == 4 && !latestInvoiceData.patientPaysTotalFee // and encounter status = in progress & patientPaysTotalFee = false
            )
            || //or
            (
                (
                    [15, 16, 17, 25, 26, 18, 30].includes(latestInvoiceData.patientInsuranceType) || 
                    (latestInvoiceData.patientInsuranceType == 10 && !latestInvoiceData.patientInsuranceId) //Insurance type = anything else or overseas other fund
                ) && 
                patientEncounterDetails.billingStatus == 4 //and encounter status = in progress
            )
        ) && patientEncounterDetails.claimStatus == 5; //claim status is rejected


    /*
        show this btn if:
        1. Insurance type is PRIVATE
        2. No (received or received externally) payments paid by patient under in any invoices
        3. Encounter status is NOT 5-Debt Collection
        4. Claim status is either Pending, Failed, Rejected or NoNeedToClaim
        5. OPV passed for Medicare (may or may not pass Health Fund)
        6. Encounter billing status is >= 2 && < 5
        7. Encounter has to have active invoice(s)
    */
    $: showBulkBillAndRecordBadDebtBtn =
        latestInvoiceData.insuranceType == DirectInsuranceType.Private &&
        (!paymentsData.length ||
            !paymentsData
                .filter((payment) => payment.paidBy == 0)
                ?.some((payment) => [1, 2].includes(payment.status))) &&
        patientEncounterDetails.status != 5 &&
        [0, 1, 5, 10].includes(patientEncounterDetails?.claimStatus) &&
        patientEncounterDetails.opvStatusMedicare == 1 &&
        patientEncounterDetails.billingStatus >= 2 &&
        patientEncounterDetails.billingStatus < 5 &&
        !!invoicesData.length &&
        invoicesData.some((invoice) => invoice.status != 9);

    // ignore trigger RemoveEncounterClaimDelay api if invoice billing data has patientPaysTotalFee = true, OR if encounter already has active claim (valid claimTransactionId)
    $: skipRemoveEncounterClaimDelay =
        latestInvoiceData?.patientPaysTotalFee ||
        patientEncounterDetails.claimTransactionId;

    $: showCreateAndSendInvoiceWorkflowButton = latestInvoiceData?.patientPaysTotalFee && !patientEncounterDetails.patientClaim;

    let isDownloadingInvoice = false,
        isRegeneratingInvoice = false,
        isCreateInvoiceModalOpen = false,
        creatingInvoice = false;

    let isSetInvoicePaperModalOpen = false,
        selectedInvoiceID = Number(0),
        isSettingInvoicePaper = false;

    let isDiscardItemsModalOpen = false,
        discardItemsChange = () => {};

    let isCancelInvoiceModalOpen = false,
        isCancellingInvoice = false;

    let isClaimingInvoice = false,
        isClaimDoneModalOpen = false,
        isClaimFailModalOpen = false,
        isClaimRequiresSendingInvoice = false,
        claimDoneText = "",
        claimFailStatus = Number(0);

    let confirmCreateInvoiceWithInsufficientTimeModalOpen = false;

    let sendInvoiceToPatientModalOpen = false,
        sendInvoiceToSupportModalOpen = false,
        isSendingInvoiceToSupport = false,
        selectedInvoiceData = {};

    let isOverrideClaimModalOpen = false;

    let isPatientClaimModalOpen = false,
        isPatientClaiming = false;

    let isBulkBillAndRecordBadDebtModalOpen = false,
        bulkBillNote = "",
        isUpdatingBulkBillAndRecordBadDebt = false;

    let isRemoveClaimDelayModalOpen = false,
        isRemovingClaimDelay = false;

    let claimFunction = () => {},
        showClaimMissingTimeCodeConfirmModal = false,
        claimActionName = "";

    const showClaimInvoiceInsuranceTypes = [0, 11, 20];

    const showClaimViaSendingInvoiceInsuranceTypes = [16, 17, 18, 25, 26, 30];

    let claimErrorModalOpen = false,
        claimErrorWithErrMsg = false,
        claimErrorMsg = "";

    $: btnOptions = [
        {
            iconName: "createTopupInvoiceIcon",
            iconFunction: () => _openCreatePartialPaymentModal(), //add logic
            iFont: "fa fa-square-up-right",
            btnText: "Create Topup Invoice",
            isHidden: !showCreateTopupInvoiceBtn, 
        },
        {
            iconName: "insuranceDeclinedIcon",
            iconFunction: () => _openRequestInsuranceDeclineModal(), //add logic
            iFont: "fa fa-square-minus",
            btnText: "Insurance Declined",
            isHidden: !showInsuranceDeclinedButton, 
        },
        {
            iconName: "patientClaimIcon",
            iconFunction: () => _openPatientClaimModal(),
            iFont: "fa fa-fax",
            btnText: "Patient Claim",
            isHidden: !showPatientClaimBtn,
        },
        {
            iconName: "sendInvoice",
            iconFunction: () => _openClaimInvoiceModal(true),
            iFont: "fa fa-fax",
            btnText: "Claim",
            hasTooltip: true,
            tooltip: hasOPVFailed
                ? "Unable to claim - patient verification failed"
                : "Submit Claim to Eclipse",
            isHidden: !showClaimViaSendingInvoiceBtn,
            isDisabled: (showClaimInvoiceBtn && hasOPVFailed) || isClaimingInvoice,
        },
        {
            iconName: "bulkBillAndRecordBadDebt",
            iconFunction: () => _openBulkBillAndRecordBadDebtModal(),
            iFont: "fa-solid fa-file-circle-exclamation",
            btnText: "Bulk Bill & Record Bad Debt",
            isHidden: !showBulkBillAndRecordBadDebtBtn,
        },
        {
            iconName: "createIcon",
            iconFunction: () => _openCreateInvoiceModal(),
            btnText: "Create",
            iFont: addIcon,
            hasTooltip: true,
            tooltip: "Create Invoice",
            isHidden: !showCreateInvoiceBtn,
        },
        {
            iconName: "claimStatusTooltip cursor-default",
            btnText: `Claim ${getClaimStatus(patientEncounterDetails.claimStatus)}`,
            iFont: "fa-solid fa-circle-exclamation",
            isHidden: !showEncounterPendingClaimTooltip(
                patientEncounterDetails,
            ),
            hasTooltip: true,
            tooltip: getEncounterClaimStatusTooltip(patientEncounterDetails),
        },
        {
            iconName: "removeClaimDelay",
            iFont: "fa-regular fa-circle-xmark",
            isHidden: !showEncounterRemoveClaimDelayIcon(
                patientEncounterDetails,
            ),
            hasTooltip: true,
            tooltip: "Remove claim delay",
            iconFunction: () => _openRemoveClaimDelayModal(),
        },
    ];

    $: columns = [
        // {
        //     key: "deleteBtn",
        //     title: "",
        //     headerClass: "text-left pl-3 cursor-default rounded-tl-xl",
        //     class: "text-left px-3 cursor-pointer flex flex-1 items-center",
        //     renderValue: (v) => _renderDeleteBtn(v),
        // },
        {
            key: "invoiceId",
            title: "ID",
            headerClass: "text-left pl-3 cursor-default w-[5%] rounded-tl-xl",
            class: "text-left px-3 cursor-pointer w-[5%]",
            renderValue: (v) => getNumber(v.id),
        },
        {
            key: "invoiceNumber",
            title: "Invoice Number",
            headerClass: "text-left pl-3 cursor-default w-[15%]",
            class: "text-left px-3 cursor-pointer w-[15%]",
            renderValue: (v) => getText(v.invoiceNumber),
        },
        {
            key: "createDate",
            title: "Create Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => getFormatDate(v.createdDate),
        },
        {
            key: "dueDate",
            title: "Due Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => getFormatDate(v.dueDate),
        },
        {
            key: "gapFee",
            title: "Gap Fee",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getAmountInCurrency(v.actualGap),
        },
        {
            key: "rebate",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getAmountInCurrency(v.rebate),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            value: (v) =>
                getAmountInCurrency(
                    v.addGST ? (v.actualGap + v.rebate) * 0.1 : 0,
                ),
        },
        {
            key: "processingFee",
            title: "Processing Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.internalProcessingFee ?? 0),
            headerConfig: {
                showTooltip: true,
                tooltipContent: "Internal Processing Fee",
            },
        },
        {
            key: "totalFee",
            title: "Total Fee",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getDisplayTotal(v),
        },
        {
            key: "payments",
            title: "Invoice Payments",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getPaymentAmountPaid(v.payments),
        },
        {
            key: "totalPayments",
            title: "Total Payments",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: () => getAmountInCurrency(totalPaymentsAmount),
        },
        {
            key: "invoiceStatus",
            title: "Status",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getStatus(v.status),
        },
        {
            key: "buttons",
            title: "",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: CollapsibleTableIcons,
                props: {
                    icons: [
                        "downloadLinkIcon",
                        "printIcon",
                        "downloadIcon",
                        "reGenerateIcon",
                        "sendToPatientIcon",
                        "downloadHealthIcon",
                        "deleteInvoiceIcon",
                        "goToPortalPageIcon",
                    ],
                    tableType: "Invoice",
                    insuranceType: latestInvoiceData?.insuranceType,
                    downloadOnlyStatus: 9,
                    // showCancelledBanner: true,
                    cancelledStatus: 9,
                    showPatientInDownload: PatientPayInsuranceTypes.includes(
                        patientEncounterDetails.patient?.insuranceType,
                    ),
                    hideSendToPatientIcon:
                        showClaimViaSendingInvoiceInsuranceTypes.includes(
                            latestInvoiceData?.insuranceType,
                        ),
                },
            },
        },
    ];

    let isCreatePartialPaymentModalOpen = false,
        isRequestInsuranceDeclineModalOpen = false,
        isCreatingPartialPaymentInvoice = false,
        isRequestingInsuranceDeclined = false;


    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "buttons") {
            if (classList.contains("downloadIcon")) downloadInvoice(rowData.id);
            else if (classList.contains("reGenerateIcon"))
                regenerateInvoice(rowData);
            else if (className.includes("goToPortalPageIcon"))
                openPortalPage(rowData, patientEncounterDetails.patient);
            else if (classList.contains("downloadLinkIcon"))
                goToInvoice(rowData.id);
            else if (classList.contains("sendToPatientIcon"))
                _openSendInvoiceToPatientModal(rowData);
            else if (classList.contains("printIcon"))
                _openSetInvoicePaperModal(rowData);
            else if (classList.contains("downloadHealthIcon")) {
                // if (
                //     latestInvoiceData?.insuranceType ==
                //     DirectInsuranceType.Overseascover
                // )
                //     _downloadHealthFundInvoice(rowData.id);
                // else _openConfirmSendToSupportModal(rowData);
                if (
                    $user.role == DirectUserRoles.Doctor &&
                    latestInvoiceData?.insuranceType ==
                        DirectInsuranceType.Overseascover
                )
                    _openConfirmSendToSupportModal(rowData);
                else _downloadHealthFundInvoice(rowData.id);
            } else if (classList.contains("deleteInvoiceIcon"))
                _openCancelInvoiceModal(rowData.id);
        } else if (rowData.id) {
            if (itemsNotSaved)
                openDiscardItemsModal(() => {
                    goToURL(`/Invoices/view/${rowData.id}`);
                });
            else goToURL(`/Invoices/view/${rowData.id}`);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const downloadInvoice = (id, isRegenerate) => {
        goToDownloadPage(1, id, isRegenerate);
    };

    const setInvoicePaper = async () => {
        isSettingInvoicePaper = true;

        await apiMethods.putMethod
            .sendPaperInvoice(selectedInvoiceID, paperProcessingFee)
            .then((res) => _handleSendPaperInvoice(res))
            .catch((err) => showException(err));

        _closeSetInvoicePaperModal();

        isSettingInvoicePaper = false;
    };

    const regenerateInvoice = async (data) => {
        const { id, doctorId, patientId, patientEncounterId } = data;
        const { isNoGapProgram } = patientEncounterDetails;

        const regenerateObj = {
            type: isNoGapProgram ? 19 : 1,
            entityId: id,
            doctorId: doctorId,
            patientId: patientId,
            patientEncounterId: patientEncounterId,
        };

        isRegeneratingInvoice = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadInvoice(id, true);
                else
                    showError(
                        "Unable to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Unable to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingInvoice = false;
    };

    const goToInvoice = (id) => {
        // const url = `https://portal.tcihealth.com.au/?i=${id}`;
        const url = formatPortalUrls("i", id);
        window.open(url, "_blank");
    };

    const createInvoice = async (sendInvoiceAfterCreation = false, customDueDate) => {
        creatingInvoice = true;

       /*  
       let needsUpdateEncounter = false;
       if(latestInvoiceData.customDueDate != claimDelayProps.customDueDate) {
            //set custom due date
            //save due date
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == latestInvoiceData.id) {
                    if (claimDelayProps.customDueDate)
                        bd.customDueDate = getDateForBackend(
                            claimDelayProps.customDueDate,
                        );

                    return bd;
                } else return bd;
            });
            needsUpdateEncounter = true;
        }

        if(sendWithDelay) {
            //set claim delay hours
            patientEncounterDetails.claimDelayHours = claimDelayProps.claimDelayHours;
            if(claimDelayProps.claimDelayHours > 0) {
                patientEncounterDetails.billingStatus = 3; //submitted
            }
            needsUpdateEncounter = true;
        }

        if(needsUpdateEncounter){
            //update claim delay hours on the encounter, and custom due date
            var res = await apiMethods.putMethod.updatePatientEncounter(patientEncounterDetails, false, `Update claim delay / invoice due date / encounter moved to submitted.`);
            if (!res || res.error) {
                showError(
                    `Failed to update invoice claim details and due date, please refresh the page and try again and contact support if the issue persists.`,
                );
            }
        } */

         if(customDueDate && customDueDate != latestInvoiceData.customDueDate) {
            //need to update quote due date
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == latestInvoiceData.id) {
                    if (customDueDate)
                        bd.customDueDate = getDateForBackend(
                            customDueDate,
                        );

                    return bd;
                } else return bd;
            });
            //update invoice due date
            var res = await apiMethods.putMethod.updatePatientEncounter(patientEncounterDetails, false, `Update invoice due date before creation`);
            if (!res || res.error) {
                showError(
                    `Failed to update invoice due date, please refresh the page and try again and contact support if the issue persists.`,
                );
            }
        }

        await apiMethods.postMethod
            .createInvoiceFromBillingItem(latestInvoiceData.id, patientEncounterDetails.isNoGapProgram)
            .then(
                async (res) =>
                    await _handleCreateInvoiceFromBillingItem(
                        res,
                        sendInvoiceAfterCreation,
                        sendWithDelay
                    ),
            )
            .catch((err) => showException(err));
      
       
        creatingInvoice = false;

        _closeCreateInvoiceModal();
    };

    const _downloadHealthFundInvoice = async (id) => {
        isDownloadingInvoice = true;

        await apiMethods.postMethod
            .createInvoiceForHealthFund(id)
            .then((res) => {
                if (res.id) downloadInvoice(id);
                else
                    showError(
                        "Unable to download this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showException(err));

        isDownloadingInvoice = false;
    };

    const openDiscardItemsModal = (callback) => {
        if (!callback) closeDiscardItemsModal();

        isDiscardItemsModalOpen = true;

        discardItemsChange = callback;
    };

    const closeDiscardItemsModal = () => (isDiscardItemsModalOpen = false);

    const cancelInvoice = async () => {
        isCancellingInvoice = true;

        await apiMethods.putMethod
            .updateInvoiceStatus(selectedInvoiceID, 9)
            .then((res) => {
                if (res.error) showError(res.error);
                else reloadPatientEncounter();
            })
            .catch((err) => showException(err));

        isCancellingInvoice = false;

        _closeCancelInvoiceModal();
    };

    const claimInvoice = async (overrideClaim = false) => {
        if (isClaimingInvoice) return;
        isClaimingInvoice = true;

        if (!isClaimRequiresSendingInvoice) {
            const { claimTransactionId, claimStatus } = patientEncounterDetails;

            // show confirm override claim modal if encounter has claimTransactionId and its claimStatus is not 5(rejected)
            if (!overrideClaim && claimTransactionId && claimStatus != 5) {
                _openOverrideClaimModal();
                isClaimingInvoice = false;
                return;
            }

            _closeOverrideClaimModal();

            await claimPatientEncounter(overrideClaim);
        } else await sendInvoiceToClaim();

        _closeClaimInvoiceModal();
    };

    const claimPatientEncounter = async (overrideClaim = false) =>
        await apiMethods.postMethod
            .claimPatientEncounter(
                {
                    patientEncounterId: patientEncounterDetails.id,
                },
                overrideClaim,
            )
            .then(async (res) => {
                // res = {
                //     patientEncounterId: 235,
                //     claimStatus: 1,
                //     claimStatusName: "Failed",
                //     accountReferenceId: "235",
                //     claimTransactionId: "TCI0000024101718352473mo",
                //     claimCorrelationTransactionId: "TCI0000024101718352473mo",
                //     id: 0,
                //     source: "",
                //     createdDate: "0001-01-01T00:00:00",
                //     updatedDate: "0001-01-01T00:00:00",
                //     deletedDate: null,
                //     changesDetails: null,
                //     error: '[{"Code":"9202","CodeType":null,"Message":"Invalid value of [123455WT] supplied for Servicing Provider Number. The value supplied must be in the format of - Provider stem (6 digit number), 1 Practice Location character, 1 Check Digit. Error located in Medical Event 01."},{"Code":"9202","CodeType":null,"Message":"Invalid value of [123455WT] supplied for Principal Provider Number. The value supplied must be in the format of - Provider stem (6 digit number), 1 Practice Location character, 1 Check Digit."}]',
                // };

                // if (res.error) {
                //     // error in this case can be either plain text or json of arr
                //     try {
                //         const errorArr = JSON.parse(res.error);
                //         if (!errorArr || !errorArr.length) throw new Error();
                //         const latestError = errorArr[0];
                //         showError(
                //             latestError.message ??
                //                 "Failed to claim, please refresh the page and try again and contact support if the issue persists.",
                //         );
                //     } catch (e) {
                //         showError(
                //             res.error ??
                //                 "Failed to claim, please refresh the page and try again and contact support if the issue persists.",
                //         );
                //     }
                // } else
                if (!res) {
                    claimErrorModalOpen = true;
                    claimErrorMsg = defaultErrorMsgPrompt;
                } else if (res.error) {
                    claimErrorModalOpen = true;
                    claimErrorWithErrMsg = true;
                    claimErrorMsg = res.error;
                } else {
                    _showClaimDoneModal(res.claimStatus);

                    await reloadBillingDetails(false, {
                        ...patientEncounterDetails,
                        claimTransactionId: res.claimTransactionId,
                    });
                }
            })
            .catch((err) => {
                claimErrorModalOpen = true;
                claimErrorMsg = defaultErrorMsgPrompt;
    });

    const sendInvoiceToClaim = async () =>
        await apiMethods.postMethod
            .sendInvoiceToClaim(invoicesData[0])
            .then(async (res) => {
                if (!res) {
                    claimErrorModalOpen = true;
                    claimErrorMsg = defaultErrorMsgPrompt;
                } else if (res.error) {
                    claimErrorModalOpen = true;
                    claimErrorWithErrMsg = true;
                    claimErrorMsg = res.error;
                }
                // if (!res || res.error)
                //     showError(
                //         res.error ??
                //             "Failed to send invoice to claim, please refresh the page and try again and contact support if the issue persists.",
                //     );
                else {
                    setTimeout(async () => {
                        await reloadBillingDetails();
                    }, 600);

                    const insurerName = [
                        {
                            id: 16,
                            name: "WorkCover",
                        },
                        {
                            id: 17,
                            name: "Traffic Accident",
                        },
                        {
                            id: 18,
                            name: "Third Party",
                        },
                        {
                            id: 25,
                            name: "Australian Defence Force",
                        },
                        {
                            id: 26,
                            name: "Australian Defence Force",
                        },
                        {
                            id: 30,
                            name: "Public in Private",
                        },
                    ];

                    _showClaimDoneModal(
                        // "The invoice was sent for claiming.",
                        `The invoice was sent to the ${insurerName.find((i) => i.id == latestInvoiceData.insuranceType)?.name} insurer`,
                        false,
                    );
                }
            })
            .catch((err) => {
                claimErrorModalOpen = true;
                claimErrorMsg = defaultErrorMsgPrompt;
    });

    const handleAfterSendInvoiceToPatient = async () => {
        if (!skipRemoveEncounterClaimDelay) {
            let response = patientEncounterDetails;

            await apiMethods.putMethod
                .removeEncounterClaimDelay(patientEncounterDetails.id)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to remove encounter claim delay, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else response = res;
                });
            
        }
        await reloadBillingDetails(true);

        _closeSendInvoiceModal();
    };

    const forwardOverseasBillToSupport = async () => {
        isSendingInvoiceToSupport = true;

        await apiMethods.postMethod
            .sendInvoiceToSupport(selectedInvoiceData)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to forward selected invoice to support, please refresh the page and try again and contact support if the issue persists.",
                    );
                else showInfo("Invoice forwarded to support successfully!");
            });

        isSendingInvoiceToSupport = false;

        _closeSendInvoiceModal();
    };

    const confirmPatientClaim = async () => {
        isPatientClaiming = true;

        if (
            patientEncounterDetails.billingStatus !=
            DirectBillingStatus.Completed
        )
            await apiMethods.putMethod.updateBillingStatus(
                patientEncounterDetails.id,
                DirectBillingStatus.Completed,
            );

        if (
            patientEncounterDetails.claimStatus ==
            DirectClaimStatus.NoNeedToClaim
        )
            await apiMethods.putMethod.updateClaimStatus(
                patientEncounterDetails.id,
                DirectClaimStatus.Pending,
            );

        await apiMethods.postMethod
            .claimPatientEncounter({
                patientEncounterId: patientEncounterDetails.id,
            })
            .then(async (res) => {
                if (!res) {
                    claimErrorModalOpen = true;
                    claimErrorMsg = defaultErrorMsgPrompt;
                } else if (res.error) {
                    claimErrorModalOpen = true;
                    claimErrorWithErrMsg = true;
                    claimErrorMsg = res.error;
                } else {
                    _showClaimDoneModal(res.claimStatus, false);
                    setTimeout(async () => {
                        await reloadBillingDetails(false);
                    }, 600);
                }
            })
            .catch((err) => {
                claimErrorModalOpen = true;
                claimErrorMsg = defaultErrorMsgPrompt;
            });

        isPatientClaiming = false;

        _closePatientClaimModal();
    };

    const handleConfirmBulkBillAndRecordBadDebt = async () => {
        isUpdatingBulkBillAndRecordBadDebt = true;

        await apiMethods.putMethod
            .updateEncounterBulkBillAndBadDebt(patientEncounterDetails.id)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Something went wrong, please refresh the page and try again and contact support if the issue persists.",
                    );
                else if (bulkBillNote) {
                    await apiMethods.postMethod.createNote({
                        relTo: NoteTypes.PatientEncounter,
                        relId: patientEncounterDetails.id,
                        content: bulkBillNote,
                        visibleToDoctors: true,
                    });
                    await apiMethods.postMethod.createNote({
                        relTo: NoteTypes.Patient,
                        relId: patientEncounterDetails?.patient?.id,
                        content: bulkBillNote,
                        visibleToDoctors: true,
                    });
                }
            });

        isUpdatingBulkBillAndRecordBadDebt = false;

        _closeBulkBillAndRecordBadDebtModal();

        reloadPatientEncounter();
    };

    const handleRemoveClaimDelay = async () => {
        isRemovingClaimDelay = true;

        await apiMethods.putMethod.removeEncounterClaimDelay(
            patientEncounterDetails.id,
        );

        isRemovingClaimDelay = false;

        _closeRemoveClaimDelayModal();

        setTimeout(async () => {
            await reloadBillingDetails(false);
        }, 600);
    };

    const updateItemNotes = async () => {
        //update item notes on send invoice, if not already added correctly

        const { useAsaCodes, mbsData, asaData, startDate, endDate } =
            latestInvoiceData;
        const { hospital, surgeon, typeOfBill } = patientEncounterDetails;

        if (useAsaCodes && (!asaData || !asaData.length)) return;

        if (!useAsaCodes && (!mbsData || !mbsData.length)) return;

        const promises = [];

        var times = "";
        if(startDate && endDate)
            times = `${getHHMM(startDate)} - ${getHHMM(endDate)}`;

        if (useAsaCodes)
            asaData.map((item) => {
                try {
                    var note = "";
                    if(typeOfBill == 1 && item.infoType == "Consults"){
                        //post op consult item, add item start end on notes
                        if(["CA060", "CA070"].includes(item.itemCode)) {
                            if(times){
                                note = times;
                            }
                        }
                    } else if(typeOfBill == 2 && (["CV120", "CV122", "CV123", "CV124"].includes(item.itemCode) || ["CV125", "CV126"].includes(item.itemCode))) {
                        //anaesthetic item, add item start end on notes
                        if(times){
                            note = times;
                        }
                    }

                    if(note != item.encounterItemDescription) { 
                        promises.push(
                            apiMethods.putMethod.updateEncounterAsaItemDescription({
                                ...item,
                                encounterId: patientEncounterDetails.id,
                                billingDataId: latestInvoiceData.id,
                                encounterItemDescription: `${note}`,
                            }),
                        );
                    }
                }
                catch(e) {
                    console.log("error updating asa item notes", e);
                }
            });
        else
            mbsData.map((item) => {
                try {
                    var note = "";
                    if(typeOfBill == 1 && item.infoType == "Consults"){
                        //post op consult item, add item start end on notes for all consult codes
                        if(!surgeon){
                            note = "Rounds in Hospital";
                        } else {
                            note = !!hospital?.id
                                ? "Referral within hospital"
                                : "Referral outside hospital";
                            if(times)
                                note += `, ${times}`;
                        }

                    } else if(typeOfBill == 2 && ([18226, 18216, 18219, 18227].includes(item.itemNum) || [18222, 18225].includes(item.itemNum))) {
                        //labour epidural insertion or topup item, add item start end on notes
                        if(startDate && endDate) {
                            //add first hour to 18216 and 18226, only if 18219 and 18227 exists respectively
                            if((item.itemNum == 18226 && mbsData.some((c) => c.itemNum == 18227)) ||
                                (item.itemNum == 18216 && mbsData.some((c) => c.itemNum == 18219))) {
                                    note = `${getHHMM(startDate)} - ${getHHMM(addHoursToDate(startDate, 1))}`;
                                } else {
                                    //add the rest or everything to the other code
                                    var isLEEndItemCode = item.itemNum == 18227 || item.itemNum == 18219;
                                    note = `${getHHMM(isLEEndItemCode ? addHoursToDate(startDate, 1) : startDate)} - ${getHHMM(endDate)}`;
                                }

                        }
                    } else if ((item.itemNum == 22012 || item.itemNum == 22014) && item.subItemNum == 0) {
                        //art monitor
                        note = "BPM systemic arterial";
                    } else if ((item.itemNum == 22012 || item.itemNum == 22014) && item.subItemNum == 1) {
                        //cvc monitor
                        note = "BPM central venous";
                    } else if ((item.itemNum == 22012 || item.itemNum == 22014) && item.subItemNum == 2) {
                        //pac monitor
                        note = "BPM pulmonary arterial";
                    }

                    //if note needs to change
                    if(note != item.encounterItemDescription) {
                        promises.push(
                            apiMethods.putMethod.updateEncounterMbsItemDescription({
                                ...item,
                                encounterId: patientEncounterDetails.id,
                                billingDataId: latestInvoiceData.id,
                                encounterItemDescription: `${note}`,
                            }),
                        );
                    }
                }
                catch(e) {
                    console.log("error updating mbs item notes", e);
                }
            });

        if (promises?.length) {
            await Promise.all(promises);

            setTimeout(async () => {
                await reloadBillingDetails(false);
            }, 600);
        }
    };

    const sentInvoiceToggleDefaultValue = () => {
        //1.⁠ ⁠Prepay total fee without PCI
        if(latestInvoiceData.oopType == 0 & latestInvoiceData.patientPaysTotalFee && !patientEncounterDetails.patientClaim) {
            return true;
        }
        //2.⁠ ⁠⁠PostPay total fee without PCI
        if(latestInvoiceData.oopType == 1 & latestInvoiceData.patientPaysTotalFee && !patientEncounterDetails.patientClaim) {
            return true;
        }
        //3.⁠ ⁠⁠PostPay gap fee
        if(latestInvoiceData.oopType == 1 & !latestInvoiceData.patientPaysTotalFee) {
            return true;
        }

        return false;
    }

    // HELPER FUNCTIONS

    const _openCreatePartialPaymentModal = () =>
        (isCreatePartialPaymentModalOpen = true);

    const _closeCreatePartialPaymentModal = () =>
        (isCreatePartialPaymentModalOpen = false);

    const _openRequestInsuranceDeclineModal = () =>
        (isRequestInsuranceDeclineModalOpen = true);

    const _closeRequestInsuranceDeclineModal = () =>
        (isRequestInsuranceDeclineModalOpen = false);

    const createTopUpInvoice = async () => {
        isCreatingPartialPaymentInvoice = true;

        await apiMethods.postMethod
            .sendTopUpInvoice(patientEncounterDetails.id)
            .then((res) => _handleCreateTopUpInvoice(res))
            .catch((err) => showException(err));

        _closeCreatePartialPaymentModal();

        isCreatingPartialPaymentInvoice = false;
    };

    const sendInsuranceDeclineRequest = async () => {
        isRequestingInsuranceDeclined = true;

        await apiMethods.putMethod
            .insuranceDeclined(patientEncounterDetails.id)
            .then((res) => _handleInsuranceDeclineRequest(res))
            .catch((err) => showException(err));

        _closeRequestInsuranceDeclineModal();

        isRequestingInsuranceDeclined = false;
    };

    const _handleCreateTopUpInvoice = async (res) => {
        if (!res || res.error)
            showError(
                "Failed to create topup invoice, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            await reloadBillingDetails();
            isPartialPaymentCreated = true;
        }
    };

    const _handleInsuranceDeclineRequest = async (res) => {
        if (!res || res.error || res.toString().toLowerCase() == "false")
            showError(
                res.error ??
                    "Failed to request insurance decline, please refresh the page and try again and contact support if the issue persists.",
            );
        else await reloadBillingDetails();
    };

    const _openCreateInvoiceModal = async () => {
        const isSufficientTimeToCreateInvoices =
            isSufficientTimeToCreateEstimatesOrInvoices(
                patientEncounterDetails,
            );
        
        const isHbfWaFullyCoveredWithPatientPaymentRequest = await isHbfWAFullyCoveredWithPatientPayment(patientEncounterDetails, latestInvoiceData);
        if(isHbfWaFullyCoveredWithPatientPaymentRequest) {
            isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = true;
            return;
        }

        if (!_checkTimeCodeMissing()) {
            if (isSufficientTimeToCreateInvoices) {
                isCreateInvoiceModalOpen = true;
                sendInvoice = sentInvoiceToggleDefaultValue();
            }
            else {
                _closeCancelInvoiceModal();
                confirmCreateInvoiceWithInsufficientTimeModalOpen = true;
            }
        } else if (isSufficientTimeToCreateInvoices)
            _openClaimMissingTimeCodeConfirmModal(
                () => { 
                    isCreateInvoiceModalOpen = true;
                    sendInvoice = sentInvoiceToggleDefaultValue();
                },
                "create the invoice",
            );
        else
            _openClaimMissingTimeCodeConfirmModal(() => {
                _closeCancelInvoiceModal();
                confirmCreateInvoiceWithInsufficientTimeModalOpen = true;
            }, "create the invoice");
    };

    const _closeCreateInvoiceModal = () => {
        isCreateInvoiceModalOpen = false;
        confirmCreateInvoiceWithInsufficientTimeModalOpen = false;
        sendInvoice = false;
    };

    const _openSetInvoicePaperModal = async (row) => {
        hasInternalProcessingFee = false;
        estimateProcessingFee = 0;
        if (row.internalProcessingFee) {
            // 9 being cancelled estimate/quote;
            const estimate = patientEncounterDetails.estimates?.find(
                (e) => e.status !== 9,
            );
            if (estimate && estimate.internalProcessingFee) {
                estimateProcessingFee = estimate.internalProcessingFee;
                paperProcessingFee =
                    row.internalProcessingFee - estimateProcessingFee;
            } else {
                paperProcessingFee = row.internalProcessingFee;
            }
            hasInternalProcessingFee = paperProcessingFee > 0;
        } else if (row.groupId) {
            if (!$allGroups.length) await cacheAllGroups();
            const group = $allGroups.find((g) => g.id == row.groupId);
            paperProcessingFee =
                group?.paperProcessingFee ??
                EmptyGroupDetails.paperProcessingFee;
        } else paperProcessingFee = EmptyGroupDetails.paperProcessingFee;

        selectedInvoiceID = Number(row.id);
        isSetInvoicePaperModalOpen = true;
    };

    const _openBulkBillAndRecordBadDebtModal = () =>
        (isBulkBillAndRecordBadDebtModalOpen = true);

    const _closeBulkBillAndRecordBadDebtModal = () => {
        isBulkBillAndRecordBadDebtModalOpen = false;
        bulkBillNote = "";
    };

    const _closeSetInvoicePaperModal = () => {
        selectedInvoiceID = Number(0);
        isSetInvoicePaperModalOpen = false;
    };

    const _handleSendPaperInvoice = (res) => {
        if (!res || res.error)
            showError(
                "Failed to request paper copy of invoice, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.id) {
            downloadInvoice(res.id);
             setTimeout(async () => {
                await reloadBillingDetails();
            }, 600);
        }
    };

    const _handleCreateInvoiceFromBillingItem = async (
        res,
        sendInvoiceAfterCreation = false,
        sendWithDelay = false
    ) => {
        if (res.error) showError(res.error);
        else {

            if (sendInvoiceAfterCreation && (!sendWithDelay || (sendWithDelay && claimDelayProps.claimDelayHours == 0))) {
                // show time selector before send invoice to patient after creation
                const sendInvoiceDirectlyToPatient = [0, 11].includes(
                    latestInvoiceData.insuranceType,
                );
                _closeCreateInvoiceModal();
                await _openSendInvoiceToPatientModal({
                    ...res,
                    skipDelay: !sendInvoiceDirectlyToPatient,
                });
            }
            await reloadBillingDetails();

        }
    };

    const _openCancelInvoiceModal = (id) => {
        isCancelInvoiceModalOpen = true;
        selectedInvoiceID = Number(id);
    };

    const _closeCancelInvoiceModal = () => {
        isCancelInvoiceModalOpen = false;
        selectedInvoiceID = Number(0);
    };

    const _openClaimInvoiceModal = (requireSendingInvoice = false) => {
        isClaimRequiresSendingInvoice = requireSendingInvoice;
        if (!_checkTimeCodeMissing()) claimInvoice();
        else
            _openClaimMissingTimeCodeConfirmModal(
                () => claimInvoice(),
                "claim",
            );
    };

    const _closeClaimInvoiceModal = () => {
        isClaimingInvoice = false;
        isClaimRequiresSendingInvoice = false;
    };

    const _openRemoveClaimDelayModal = () => {
        if (!_checkTimeCodeMissing()) isRemoveClaimDelayModalOpen = true;
        else
            _openClaimMissingTimeCodeConfirmModal(
                () => (isRemoveClaimDelayModalOpen = true),
                "claim",
            );
    };

    const _closeRemoveClaimDelayModal = () =>
        (isRemoveClaimDelayModalOpen = false);

    const _openPatientClaimModal = () => {
        if (!_checkTimeCodeMissing()) isPatientClaimModalOpen = true;
        else
            _openClaimMissingTimeCodeConfirmModal(
                () => (isPatientClaimModalOpen = true),
                "claim",
            );
    };

    const _closePatientClaimModal = () => (isPatientClaimModalOpen = false);

    const _openClaimMissingTimeCodeConfirmModal = (
        fn = () => {},
        actionName = "",
    ) => {
        showClaimMissingTimeCodeConfirmModal = true;
        claimFunction = () => fn();
        claimActionName = actionName;
    };

    const _closeClaimMissingTimeCodeConfirmModal = () => {
        showClaimMissingTimeCodeConfirmModal = false;
        claimFunction = () => {};
        claimActionName = "";
    };

    const _showClaimDoneModal = (claimStatus, reloadRequired = true) => {
        isClaimDoneModalOpen = [0, 2, 3, 10].includes(claimStatus);
        isClaimFailModalOpen = [1, 5].includes(claimStatus);
        claimDoneText = "";

        // reset logsLoaded to false, thus redirect to logs tab will trigger logs refresh
        logsLoaded = false;

        // 1 and 5 in Error Modal
        switch (claimStatus) {
            case 0:
                claimDoneText = "Successfully scheduled to claim.";
                break;
            case 2:
                claimDoneText = "Successfully submitted claim.";
                break;
            case 3:
                claimDoneText = "Successfully claimed.";
                break;
            case 10:
                claimDoneText = "No need to claim for this encounter.";
                break;
            case 1:
            case 5:
                claimFailStatus = Number(claimStatus);
                claimDoneText =
                    "Unable to claim. Check the encounter logs for more information and contact support if necessary.";
                break;
            default:
                isClaimDoneModalOpen = true;
                claimDoneText = claimStatus;
                break;
        }

        if (reloadRequired)
            apiMethods.putMethod
                .updatePatientEncounter({
                    ...patientEncounterDetails,
                    claimStatus: 0,
                    billingStatus: 4,
                }, false, "Show claim done")
                .then(() => reloadBillingDetails());
    };

    const _closeClaimFailModal = () => {
        isClaimFailModalOpen = false;
        claimFailStatus = Number(0);
    };

    const _getStatus = (status) => {
        let statusText, statusColor;

        statusText = getInvoiceStatus(status);

        switch (status) {
            case 1:
            case 2:
                statusColor = "#2DBD6F";
                break;
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const _closeClaimDoneModal = () => {
        isClaimDoneModalOpen = false;
        claimDoneText = "";
    };

    const isQuotePrepayAndPatientPaysTotalFeeButInvoiceRebatesBigger = async () => {
        const { estimates, billingData } = patientEncounterDetails;
        //get estimate
        if(estimates && estimates.length > 0){
            var estimate = estimates.find((e) => !e.isExpired);
            if(estimate){
                //get billing data linked to estimate
                var quoteData = billingData.find((bd) => bd.id == estimate.patientEncounterBillingDataId);
                if(quoteData && quoteData.oopType == 0 && quoteData.patientPaysTotalFee){
                    //check quote totals against invoice
                    if(quoteData.totalFee < latestInvoiceData.totalFee){
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const anItemFeeIsLowerThanRebate = async () => {
       if(latestInvoiceData){
            if(latestInvoiceData.billingCodesType == 0){
                //mbs
                const exists = latestInvoiceData.mbsData.some((mbs) => mbs.feeCharged < mbs.rebate);
                return exists;
            } else if(latestInvoiceData.billingCodesType == 1){
                //asa
                const exists = latestInvoiceData.asaData.some((asa) => asa.feeCharged < asa.rebate);
                return exists;
            }
        }  
       return false;
    }

    const _openSendInvoiceToPatientModal = async (data) => {
        if (
            patientEncounterDetails?.doctor?.status !==
                DirectDoctorStatus.Active &&
            patientEncounterDetails?.doctor?.status !== DirectDoctorStatus.Demo
        )
            return showPopupNotification(
                "Account not active",
                "The encounter doctor is not active. Please enable it to send this invoice.",
            );
        if (patientEncounterDetails?.status === DirectEncounterStatus.Archived)
            return showPopupNotification(
                "The encounter is archived",
                "The encounter is archived. Please un-archive it to send this invoice.",
            );
       
        if (patientEncounterDetails?.patient?.deceased)
            return showPopupNotification(
                "Patient Deceased",
                "Unable to send invoice, patient deceased.",
            );

        //check if encounter date is in the future
        const scheduledDate = new Date(patientEncounterDetails.scheduledDate);
        const today = new Date();
        scheduledDate.setHours(0, 0, 0, 0);  // Normalize both dates to start of day (00:00:00)
        today.setHours(0, 0, 0, 0);

        if(scheduledDate > today){
            return showPopupNotification(
                "Invoice cannot be sent",
                "Encounter date is in the future",
            );
        }

        if(isQuotePrepayAndPatientPaysTotalFeeButInvoiceRebatesBigger() == true){
            return showPopupNotification(
                "Invoice cannot be sent",
                "The total fee collected on quote is less than the insurance rebate. Please contact support to resolve this.",
            );
        }

        if(anItemFeeIsLowerThanRebate() == true) {
             return showPopupNotification(
                "Invoice cannot be sent",
                "An item fee is lower than item rebate, please check billing rule or contact support to resolve this.",
            );
        }

        //function in function to store the send invoice action
        var sendAction = async () => {
            await updateItemNotes();

            const insuranceTypeIsPrivateOrOverseas = [
                DirectInsuranceType.Private,
                DirectInsuranceType.Overseascover,
            ].includes(latestInvoiceData.insuranceType);
            const patientPaysTotalFee = latestInvoiceData.patientPaysTotalFee;
            const isPostPay = latestInvoiceData.oopType == 1;
            const scheduledDateIsValidAndInPast = isDateAndTimeInPast(
                patientEncounterDetails.scheduledDate,
            );
            const isPatientClaimOff = !patientEncounterDetails.patientClaim;
            const hasPatientPaymentReceived =
                !!paymentsData.length &&
                paymentsData.some(
                    (payment) =>
                        payment.paidBy == 0 &&
                        payment.status !== 4 &&
                        [1, 2].includes(payment.status),
                );

            const isNotRefundOrTopUpInvoice = data.invoiceReason !== 3 && data.invoiceReason !== 4;

            sendInvoiceToPatientModalOpen = true;

            selectedInvoiceData = {
                ...data,
                type: "invoice",
                patientPhone: patientEncounterDetails.patient?.phone ?? "",
                patientEmail: patientEncounterDetails.patient?.email ?? "",
                patientContacts:
                    patientEncounterDetails.patient?.patientContacts ?? [],
                insuranceType: latestInvoiceData.insuranceType,
                sendLatestReceipt:
                    insuranceTypeIsPrivateOrOverseas &&
                    patientPaysTotalFee &&
                    isPostPay &&
                    scheduledDateIsValidAndInPast &&
                    isPatientClaimOff &&
                    isNotRefundOrTopUpInvoice &&
                    hasPatientPaymentReceived,
            };
        }


        if (patientEncounterDetails.disableNotifications && InsuranceTypesBypassDisabledComms.includes(patientEncounterDetails.patient.insuranceType)) {
            //allow sending invoice if patient has insurance type that bypasses disabled comms, even if notifications are disabled on encounter
            await sendAction();
        }
        else {
            runWithCommunicationsCheck(patientEncounterDetails, async () => {
                await sendAction();
            });
        }
        
    };

    const _openConfirmSendToSupportModal = (data) => {
        sendInvoiceToSupportModalOpen = true;
        selectedInvoiceData = { ...data };
    };

    const _closeSendInvoiceModal = () => {
        sendInvoiceToPatientModalOpen = false;
        sendInvoiceToSupportModalOpen = false;
        selectedInvoiceData = {};
    };

    const _openOverrideClaimModal = () => {
        _closeClaimInvoiceModal();
        isOverrideClaimModalOpen = true;
    };

    const _closeOverrideClaimModal = () => (isOverrideClaimModalOpen = false);

    let paperProcessingFee,
        estimateProcessingFee,
        hasInternalProcessingFee = false;

    $: isValidPaperProcessingFee =
        paperProcessingFee >= 0 && paperProcessingFee <= MaxPaperProcessingFee;

    const _checkTimeCodeMissing = () => {
        if (!latestInvoiceData || isObjectEmpty(latestInvoiceData)) return true;

        const { billingCodesType, insuranceType, mbsData, asaTimeData } =
            latestInvoiceData;

        if (billingCodesType == 2) return false;

        const { typeOfBill } = patientEncounterDetails;

        // Check time code for Anaesthesia encounter with insurance type other than Cosmetic
        if (typeOfBill != 0 || insuranceType == DirectInsuranceType.Cosmetic)
            return false;

        if (billingCodesType == 0)
            return (
                !mbsData?.length ||
                !mbsData.some((item) => item.infoType == "Time")
            );
        else if (billingCodesType == 1) return !asaTimeData?.length;
        else return false;
    };

    let sendInvoice = false;
    let sendWithDelay = false;
    let isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = false;

    let claimDelayProps = {
        InsuranceType: latestInvoiceData.patientInsuranceType,
        claimDelayHours: patientEncounterDetails.claimDelayHours ?? Number(0),
        customDueDate: latestInvoiceData.customDueDate,
    }

    let customDueDate = latestInvoiceData.customDueDate;
</script>

<CardLayout title="Invoices" {btnOptions}>
    {#if !hasCurrentInvoice}
        {#if !latestInvoiceDataItems.length}
            <div class="grid flex-1 h-full card bg-white rounded-box">
                <div
                    class="flex flex-1 items-center justify-between px-2 py-4 w-full lg:flex-row"
                >
                    <p>No invoices created</p>
                </div>
            </div>
        {:else}
            <div class="flex flex-1 flex-col gap-y-2 px-2 py-4 w-full">
                <p>No invoices created</p>
                <p>{getDisplayBillingRuleText(latestInvoiceData)}</p>
                <p>{getDisplayPaymentDetailsText(latestInvoiceData)}</p>
            </div>
        {/if}
    {/if}
    {#if invoicesData.length}
        <div class="w-full overflow-x-auto" id="EncounterInvoiceTableContainer">
            {#key invoicesData.map(r => r.id).join('|')}
                <SvelteTable
                    tableContainerId="EncounterInvoiceTableContainer"
                    {columns}
                    rows={invoicesData}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    grayOutFunction={(row) => row.status == 9}
            />
            {/key}
        </div>
    {/if}
</CardLayout>

{#if isDownloadingInvoice}
    <LoadingModal titleText="Downloading Invoice PDF..." />
{/if}

{#if isRegeneratingInvoice}
    <LoadingModal titleText="Regenerating Invoice PDF..." />
{/if}

<NotificationModal
    title="Confirmation"
    isModalOpen={isCreateInvoiceModalOpen}
    isConfirming={creatingInvoice}
    closeModal={_closeCreateInvoiceModal}
    closeModalFunction={_closeCreateInvoiceModal}
    showConfirmBtn={false}
    contentClass={"p-6 flex flex-col"}
    btnOptions={[
        {
            text: sendInvoice ?  `Create and send ${sendWithDelay && claimDelayProps.claimDelayHours > 0 ? `in ${claimDelayProps.claimDelayHours} hours` : ''}` : 'Create',
            loading: creatingInvoice,
            function: () => createInvoice(sendInvoice, customDueDate),
        },
    ]}
>
   <!--  <PatientEncounterInvoiceSendDetails 
        encounterDetails={patientEncounterDetails}
        latestInvoiceData={latestInvoiceData} 
        bind:claimDelayProps={claimDelayProps}
        bind:sendWithDelay={sendWithDelay}
        bind:sendInvoice={sendInvoice}>
    </PatientEncounterInvoiceSendDetails> -->
     <PatientEncounterInvoiceSendDetails 
        {patientEncounterDetails}
        latestInvoiceData={latestInvoiceData} 
        bind:customDueDate={customDueDate}
        bind:sendInvoice={sendInvoice}>
    </PatientEncounterInvoiceSendDetails>
</NotificationModal>

<NotificationModal
    isModalOpen={confirmCreateInvoiceWithInsufficientTimeModalOpen}
    submitText="Continue"
    closeModal={_closeCreateInvoiceModal}
    confirmAction={createInvoice}
    isConfirming={creatingInvoice}
    closeModalFunction={_closeCreateInvoiceModal}
>
    <p class="leading-6">The patient was asked to update their details.</p>
    <p class="leading-6">Cancel update and continue?</p>
</NotificationModal>

<NotificationModal
    title="Not allowed"
    isModalOpen={isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen}
    closeModal={() => isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = false }
    showConfirmBtn={false}
    closeModalFunction={() => isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = false}
>
    <p class="leading-6">HBF (WA) Fully Covered scheme does not permit the patient to be charged a gap or a total fee.</p>
</NotificationModal>

<NotificationModal
    isModalOpen={isSetInvoicePaperModalOpen}
    title="Get paper invoice"
    cancelText="Cancel"
    submitText={isSettingInvoicePaper ? "Confirming" : "Confirm"}
    closeModal={_closeSetInvoicePaperModal}
    confirmAction={setInvoicePaper}
    isConfirming={isSettingInvoicePaper}
    closeModalFunction={_closeSetInvoicePaperModal}
    isConfirmDisabled={!isValidPaperProcessingFee}
    modalMaxWidth="max-w-xl"
>
    <div class="flex gap-x-4 items-center">
        {#if hasInternalProcessingFee}
            Change the processing fee to
        {:else}
            Add
        {/if}
        $
        <DecimalInput
            bind:value={paperProcessingFee}
            inputMin="0"
            inputMax={MaxPaperProcessingFee}
            inputClass={isValidPaperProcessingFee ? "w-16" : "input-error w-16"}
        />
        {#if !hasInternalProcessingFee}
            processing fee to the quote and download.
        {/if}
    </div>
    {#if paperProcessingFee > MaxPaperProcessingFee}
        <p class="text-error mt-5">
            The maximum allowed paper processing fee is ${MaxPaperProcessingFee}.
        </p>
    {/if}
    {#if estimateProcessingFee}
        <p class="text-secondary mt-5">
            Please note this does not include the ${estimateProcessingFee} quote
            processing fee.
        </p>
    {/if}
</NotificationModal>

<NotificationModal
    isModalOpen={isDiscardItemsModalOpen}
    closeModal={discardItemsChange}
    confirmAction={closeDiscardItemsModal}
    submitText="Cancel"
    cancelText="Discard changes"
    isReverseBtns={true}
    closeModalFunction={closeDiscardItemsModal}
>
    You have unsaved items, do you want to discard all unsaved changes?
</NotificationModal>

<NotificationModal
    isModalOpen={isCancelInvoiceModalOpen}
    cancelText="Delete"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    isConfirming={isCancellingInvoice}
    isReverseBtns
    confirmAction={_closeCancelInvoiceModal}
    closeModal={cancelInvoice}
    closeModalFunction={_closeCancelInvoiceModal}
>
    <p class="text-error">Cancel this invoice?</p>
</NotificationModal>

<NotificationModal
    isModalOpen={isClaimDoneModalOpen}
    showCancelBtn={false}
    submitText="OK"
    confirmAction={_closeClaimDoneModal}
    title=""
    closeModalFunction={_closeClaimDoneModal}
>
    {claimDoneText}
</NotificationModal>

<NotificationModal
    isModalOpen={isOverrideClaimModalOpen}
    title="Override Claim"
    cancelText="Cancel"
    submitText="Claim"
    closeModal={_closeOverrideClaimModal}
    closeModalFunction={_closeOverrideClaimModal}
    confirmAction={() => claimInvoice(true)}
>
    <div class="flex flex-col gap-y-2 p-4">
        <p>The encounter already has an active claim.</p>
        <p>Only the latest claim will be able to generate a payment report.</p>
        <p>Do you want to override and make another claim?</p>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isClaimFailModalOpen}
    title={`${claimFailStatus == 1 ? "Claim Failed" : claimFailStatus == 5 ? "Claim Rejected" : "Invalid claim status"}`}
    showCancelBtn={false}
    submitText="Close"
    confirmAction={_closeClaimFailModal}
    closeModalFunction={_closeClaimFailModal}
>
    <p>{claimDoneText}</p>
</NotificationModal>

<SendQuoteOrInvoiceToPatientModal
    data={selectedInvoiceData}
    bind:patientEncounterDetails={patientEncounterDetails}
    {paymentsData}
    bind:isModalOpen={sendInvoiceToPatientModalOpen}
    afterSendFunc={handleAfterSendInvoiceToPatient}
/>

<NotificationModal
    title="Forward bill to support"
    isModalOpen={sendInvoiceToSupportModalOpen}
    cancelText="Not now"
    closeModal={_closeSendInvoiceModal}
    closeModalFunction={_closeSendInvoiceModal}
    submitText="Confirm"
    confirmAction={forwardOverseasBillToSupport}
    isConfirming={isSendingInvoiceToSupport}
>
    <div class="flex flex-col gap-y-2 p-4">
        <p>
            This bill will now be forwarded to support to claim directly from
            the patient's overseas insurer.
        </p>
        <p>
            Note that payment is subject to the patient's insurance coverage,
            and there may be a risk of underpayment or non-payment if the
            patient's coverage is insufficient.
        </p>
        <p>Do you wish to proceed?</p>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isPatientClaimModalOpen}
    title="Patient Claim"
    closeModal={_closePatientClaimModal}
    closeModalFunction={_closePatientClaimModal}
    cancelText="Cancel"
    confirmAction={confirmPatientClaim}
    submitText={isPatientClaiming ? "Claiming..." : "Claim"}
    isConfirming={isPatientClaiming}
>
    <div class="flex flex-col gap-y-2 p-4">
        <p>
            Claiming for the patient will complete the encounter and run an
            immediate patient claim.
        </p>
        <p>Continue?</p>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isBulkBillAndRecordBadDebtModalOpen}
    title="Warning"
    cancelText="Cancel"
    closeModal={_closeBulkBillAndRecordBadDebtModal}
    confirmAction={handleConfirmBulkBillAndRecordBadDebt}
    submitText="Confirm"
    closeModalFunction={_closeBulkBillAndRecordBadDebtModal}
    isConfirming={isUpdatingBulkBillAndRecordBadDebt}
>
    <div class="flex flex-col p-4 gap-y-2">
        <p>
            This action will mark the patient as a "Bad Debtor", cancel ALL
            pending quotes, invoices and pending payments, change the encounter
            to Self-insured, create a bulk bill claim for the items selected,
            and add the following note to the patient and patient encounter.
        </p>

        <RichTextEditor bind:content={bulkBillNote} />
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={showClaimMissingTimeCodeConfirmModal}
    title="No time codes found"
    showCancelBtn={false}
    showConfirmBtn={false}
    closeModalFunction={_closeClaimMissingTimeCodeConfirmModal}
    btnOptions={[
        {
            text: "Cancel",
            function: () => _closeClaimMissingTimeCodeConfirmModal(),
            class: "btn-accent",
        },
        {
            text: "Continue",
            function: () => {
                claimFunction();
                _closeClaimMissingTimeCodeConfirmModal();
            },
            class: "btn-error",
        },
    ]}
>
    <p class="leading-6 p-4">
        This encounter doesn't have any time codes. Are you sure you want to
        {claimActionName} without them?
    </p>
</NotificationModal>

<NotificationModal
    isModalOpen={isRemoveClaimDelayModalOpen}
    title="Remove Claim Delay"
    isReverseBtns
    cancelText="Claim now"
    submitText="Cancel"
    confirmAction={_closeRemoveClaimDelayModal}
    closeModalFunction={_closeRemoveClaimDelayModal}
    closeModal={handleRemoveClaimDelay}
    isConfirming={isRemovingClaimDelay}
>
    <p class="leading-6 p-4">
        {getEncounterClaimStatusTooltip(patientEncounterDetails)}. Do you wish
        to claim now?
    </p>
</NotificationModal>

<NotificationModal
    isModalOpen={claimErrorModalOpen}
    title="Claim Failed"
    closeModal={() => {
        claimErrorModalOpen = false;
        claimErrorWithErrMsg = false;
        claimErrorMsg = "";
    }}
    closeModalFunction={() => {
        claimErrorModalOpen = false;
        claimErrorWithErrMsg = false;
        claimErrorMsg = "";
    }}
    showConfirmBtn={false}
    cancelButtonClass="btn-accent"
>
    <div class="p-4">
        {#if !claimErrorWithErrMsg}
            <p class="leading-6 text-error">Unable to claim</p>
        {/if}
        <p class="leading-6 text-error whitespace-pre-line">
            {claimErrorMsg}
        </p>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isCreatePartialPaymentModalOpen}
    title="Create TopUp Invoice"
    cancelText="Cancel"
    submitText={isCreatingPartialPaymentInvoice ? "Creating" : "Confirm"}
    closeModal={_closeCreatePartialPaymentModal}
    confirmAction={createTopUpInvoice}
    isConfirming={isCreatingPartialPaymentInvoice}
>
    The health fund has underpaid {patientEncounterDetails.underPaidHealthFund > 0 ? `by $${patientEncounterDetails.underPaidHealthFund.toFixed(2)}` : ''}
    <br/>
    <br/>
    Would you like to create an invoice requesting that the patient pay the difference?
</NotificationModal>

<NotificationModal
    isModalOpen={isRequestInsuranceDeclineModalOpen}
    title="Warning"
    cancelText="Cancel"
    submitText={isRequestingInsuranceDeclined ? "Requesting" : "Confirm"}
    closeModal={_closeRequestInsuranceDeclineModal}
    confirmAction={sendInsuranceDeclineRequest}
    isConfirming={isRequestingInsuranceDeclined}
>
    The health fund has rejected the claim
    {#if patientEncounterDetails.claimLastError}
         for the following reason:
        <br/>
        <br/>
        <i>"{patientEncounterDetails.claimLastError}"</i>
    {/if}
    
    <br/>
    <br/>
    Would you like to create an invoice requesting that the patient pay the difference?
</NotificationModal>
